import { useState } from "react";
import { Alert, Box, IconButton, Link, Typography } from "@mui/material";
import { ArrowBack, Visibility, VisibilityOff } from "@mui/icons-material";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { Lock } from "lucide-react";

import apiClient from "../../api-client";
import { AlertNote } from "../../components/AlertNote";
import { Button } from "../../components/Button";
import { Checkbox } from "../../components/Checkbox";
import { CustomTextField } from "../../components/CustomTextField";
import { FormField } from "../../components/FormField";

const emptyForm = { first_name: "", last_name: "", email: "", telephone: "", password: "", confirmPassword: "", accept_terms: false };

export function RegisterForm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const invitationToken = params.get("token");
  const isPublicSignup = !invitationToken;
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault(); setError("");
    if (!isPublicSignup && (!form.first_name.trim() || !form.last_name.trim())) return setError("Indica tu nombre y apellidos.");
    if (!form.email.includes("@")) return setError("Introduce un email válido.");
    if (form.password.length < 8) return setError("La contraseña debe tener al menos 8 caracteres.");
    if (!isPublicSignup && form.password !== form.confirmPassword) return setError("Las contraseñas no coinciden.");
    if (isPublicSignup && !form.accept_terms) return setError("Debes aceptar los términos y la política de privacidad.");
    setLoading(true);
    try {
      if (isPublicSignup) {
        await apiClient.post("auth/signup/", { email: form.email, password: form.password, accept_terms: form.accept_terms });
        setSent(true);
      } else {
        await apiClient.post("users/register/", { email: form.email, first_name: form.first_name, last_name: form.last_name, telephone: form.telephone, password: form.password, invitation_token: invitationToken });
        navigate("/login", { state: { message: "Registro completado. Ya puedes iniciar sesión." } });
      }
    } catch (requestError) {
      const data = requestError.response?.data;
      // Un 500 devuelve HTML: solo se leen errores de campo si la respuesta es JSON
      const first = typeof data === "object" && data ? Object.values(data).flat()[0] : null;
      setError(first || "No se pudo completar el registro.");
    } finally { setLoading(false); }
  };

  if (sent) return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Alert severity="success">Te hemos enviado un enlace de verificación a <strong>{form.email}</strong>. <strong>Para entrar es necesario verificar tu email.</strong></Alert>
      <Typography variant="body2">Tu prueba de 15 días comenzará al verificar el correo.</Typography>
      <Link component={RouterLink} to="/login" sx={{ display: "inline-flex", alignItems: "center", gap: .5 }}><ArrowBack fontSize="small" />Volver a iniciar sesión</Link>
    </Box>
  );

  const field = (key, label, type = "text", placeholder) => (
    <FormField label={label} htmlFor={`register-${key}`}>
      <CustomTextField id={`register-${key}`} type={type} value={form[key]} onChange={(e) => update(key, e.target.value)} placeholder={placeholder} disabled={loading} />
    </FormField>
  );
  const passwordField = (key, label, placeholder) => (
    <FormField label={label} htmlFor={`register-${key}`}>
      <Box sx={{ position: "relative" }}>
        <CustomTextField id={`register-${key}`} type={showPassword ? "text" : "password"} value={form[key]} onChange={(e) => update(key, e.target.value)} placeholder={placeholder} autoComplete="new-password" disabled={loading} sx={{ paddingRight: "40px" }} />
        <IconButton aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"} onClick={() => setShowPassword((current) => !current)} disabled={loading} sx={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", padding: "4px" }}>
          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      </Box>
    </FormField>
  );

  if (!isPublicSignup) return (
    <Box component="form" onSubmit={submit} sx={{ display: "grid", gap: 1.75 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>{field("first_name", "Nombre")}{field("last_name", "Apellidos")}</Box>
      {field("email", "Email", "email", "tu@agencia.com")}
      {field("telephone", "Teléfono", "tel")}
      {passwordField("password", "Crea una contraseña", "Mínimo 8 caracteres")}{passwordField("confirmPassword", "Repite la contraseña")}
      {error && <Alert severity="error">{String(error)}</Alert>}
      <Button type="submit" size="lg" fullWidth disabled={loading} sx={{ mt: 1 }}>{loading ? "Procesando…" : "Completar registro"}</Button>
    </Box>
  );

  return (
    <Box component="form" onSubmit={submit} sx={{ display: "grid", gap: 1.75 }}>
      <AlertNote variant="info" icon={Lock}>No te pediremos tarjeta. No hay renovación automática.</AlertNote>
      {field("email", "Email profesional", "email", "tu@agencia.com")}
      {passwordField("password", "Crea una contraseña", "Mínimo 8 caracteres")}
      <Box component="label" sx={{ display: "flex", alignItems: "flex-start", gap: 1, cursor: "pointer" }}>
        <Checkbox checked={form.accept_terms} onCheckedChange={(checked) => update("accept_terms", checked)} sx={{ p: 0, mt: "1px" }} />
        <Typography sx={{ fontSize: 12, color: "#788194", lineHeight: 1.45 }}>He leído y acepto la <Link component={RouterLink} to="/privacidad" target="_blank">política de privacidad</Link> y los términos de uso.</Typography>
      </Box>
      {error && <Alert severity="error">{String(error)}</Alert>}
      <Button type="submit" size="lg" fullWidth disabled={loading} sx={{ mt: 1 }}>{loading ? "Procesando…" : "Crear mi cuenta gratuita"}</Button>
      <Typography sx={{ fontSize: 11, lineHeight: 1.5, color: "#939aaa", textAlign: "center" }}>15 días con todas las funcionalidades. Después, plan gratuito de hasta 3 clientes. Puedes elegir otro plan cuando lo necesites.</Typography>
    </Box>
  );
}
